import React from 'react';
import PageBoundary from './components/routing/PageBoundary';
import UnsavedNavigationGuard from './components/routing/UnsavedNavigationGuard';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Snackbar, Alert } from '@mui/material';
import { store } from './store';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import { hideSnackbar } from './store/slices/uiSlice';
import { getProfile } from './store/slices/authSlice';

// Pages
import Login from './pages/Login';
import MainLayout from './components/layout/MainLayout';
import ModuleRoute from './components/routing/ModuleRoute';
import MODULES from './config/moduleRegistry';
import ResultDialog from './components/common/ResultDialog';
import CompanySelectionDialog from './components/common/CompanySelectionDialog';
import DraggableDialog from './components/common/DraggableDialog';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';

const ItemMaster = React.lazy(() => import('./pages/ItemMaster'));
const LocalItemMaster = React.lazy(() => import('./pages/LocalItemMaster'));
const PRDetail = React.lazy(() => import('./pages/PRDetail'));
const PODetail = React.lazy(() => import('./pages/PODetail'));
const ApprovalLink = React.lazy(() => import('./pages/ApprovalLink'));
const POEmailAction = React.lazy(() => import('./pages/POEmailAction'));
const POBulkApproval = React.lazy(() => import('./pages/POBulkApproval'));
const theme = createTheme({
  // Floating application windows use z-index 1300. Keep all transient
  // interaction surfaces above them so dialogs, choose lists and menus are
  // never rendered behind the active window.
  zIndex: {
    modal: 1400,
  },
  palette: {
    primary: {
      main: '#F1C232',
      contrastText: '#1A1A1A',
    },
    secondary: {
      main: '#F7D14A',
      contrastText: '#1A1A1A',
    },
    background: {
      default: '#f7f7f7',
      paper: '#ffffff',
    },
    text: {
      primary: '#32363a',
      secondary: '#6a6d70',
    },
    info: { main: '#F1C232' },
  },
  typography: {
    fontFamily: 'Segoe UI, Roboto, Arial, sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiModal: {
      styleOverrides: {
        root: { zIndex: 1500 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { padding: '10px 14px', fontSize: '1rem' },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '10px 14px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '7px 12px', minHeight: 40 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { minHeight: 32, paddingTop: 4, paddingBottom: 4, fontSize: 13 },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          zIndex: 1510,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          zIndex: 1510,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          // Belize Shell header color
          backgroundColor: '#354a5f',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #cfd8dc',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
        size: 'small',
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          minHeight: 30,
          padding: '4px 12px',
          borderRadius: 3,
          textTransform: 'none',
          fontSize: 13,
        },
        containedPrimary: {
          background: 'linear-gradient(180deg, #F9E27A 0%, #F7D14A 50%, #E8B640 100%)',
          color: '#1A1A1A',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
        },
        outlinedPrimary: {
          borderColor: '#E8B640',
          color: '#C58B00'
        }
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          '& .MuiInputBase-input': {
            padding: '6px 10px',
            fontSize: 13,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 12,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '6px 10px',
          fontSize: 13,
        },
        head: {
          fontWeight: 600,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 32,
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 6,
          border: '1px solid #cfd8dc',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          fontSize: 12,
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const loc = (typeof window !== 'undefined') ? window.location : { pathname: '/', search: '' } as any;
  const ret = encodeURIComponent(`${loc.pathname}${loc.search}`);
  return isAuthenticated ? <>{children}</> : <Navigate to={`/login?return=${ret}`} />;
};

// App Content (needs to be inside Provider to use Redux)
const AppContent: React.FC = () => {
  const dispatch = useDispatch();
  const { snackbar } = useSelector((state: RootState) => state.ui);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const [showCompanyDialog, setShowCompanyDialog] = React.useState(false);

  // Ensure user profile is loaded when app mounts or after refresh
  React.useEffect(() => {
    if (isAuthenticated && !user) {
      dispatch(getProfile() as any);
    }
  }, [isAuthenticated, user, dispatch]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const projArr = Array.isArray((user as any)?.allowedprojectcodes)
        ? ((user as any).allowedprojectcodes as string[])
        : undefined;
      const ccArr = Array.isArray((user as any)?.allowedcostcenters)
        ? ((user as any).allowedcostcenters as string[])
        : undefined;
      (window as any).__allowedProjectCodes = projArr;
      (window as any).__allowedCostCenters = ccArr;
    } catch {}
  }, [user]);

  // Check if company selection is needed
  React.useEffect(() => {
    if (!isAuthenticated) {
      setShowCompanyDialog(false);
      return;
    }

    const selectedCompanyId = localStorage.getItem('selectedCompanyId');
    if (!selectedCompanyId) {
      // No company selected, show dialog
      setShowCompanyDialog(true);
    } else {
      setShowCompanyDialog(false);
    }
  }, [isAuthenticated]);

  const handleCompanySelected = (companyId: number, companyName: string, databaseName: string) => {
    try {
      localStorage.setItem('selectedCompanyId', String(companyId));
      localStorage.setItem('selectedCompanyName', companyName);
      localStorage.setItem('selectedDatabaseName', databaseName);
      setShowCompanyDialog(false);

      // Dispatch custom event for other components to listen
      window.dispatchEvent(new CustomEvent('company-changed', {
        detail: { companyId, companyName, databaseName }
      }));

      // Reload profile to get updated permissions
      dispatch(getProfile() as any);
    } catch (error) {
      console.error('Failed to save company selection:', error);
    }
  };

  const handleCloseSnackbar = () => {
    dispatch(hideSnackbar());
  };

  return (
    <>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <UnsavedNavigationGuard />
        <PageBoundary><React.Suspense fallback={<div role="status" style={{ padding: 24 }}>Loading page…</div>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/item-master" element={<ProtectedRoute><MainLayout><ItemMaster /></MainLayout></ProtectedRoute>} />
          <Route path="/local-item-master" element={<ProtectedRoute><MainLayout><LocalItemMaster /></MainLayout></ProtectedRoute>} />
          <Route path="/pr/:id" element={<ProtectedRoute><MainLayout><PRDetail /></MainLayout></ProtectedRoute>} />
          <Route path="/po/:id" element={<ProtectedRoute><MainLayout><PODetail /></MainLayout></ProtectedRoute>} />
          <Route path="/approval/:token" element={<MainLayout><ApprovalLink /></MainLayout>} />
          <Route path="/po-approval/:token" element={<POEmailAction />} />
          <Route path="/po-bulk-approval/:token" element={<POBulkApproval />} />
          <Route path="/settings/approval-templates" element={<ProtectedRoute><Navigate to="/settings/approval-flow" replace /></ProtectedRoute>} />
          <Route path="/settings/approval-migration" element={<ProtectedRoute><Navigate to="/settings/approval-flow" replace /></ProtectedRoute>} />
          {MODULES.map((item) => {
            const content = (
              <MainLayout>
                <item.component />
              </MainLayout>
            );
            const guarded = (item.moduleKey || (item.roles && item.roles.length > 0)) ? (
              <ModuleRoute moduleKey={item.moduleKey || ''} roles={item.roles}>
                {content}
              </ModuleRoute>
            ) : (
              content
            );
            return (
              <Route
                key={item.key}
                path={item.path}
                element={<ProtectedRoute>{guarded}</ProtectedRoute>}
              />
            );
          })}
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
        </React.Suspense></PageBoundary>
        <ResultDialog />
      </Router>

      <Snackbar
        open={snackbar.open && snackbar.severity !== 'error'}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DraggableDialog
        open={snackbar.open && snackbar.severity === 'error'}
        onClose={handleCloseSnackbar}
        title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><ErrorOutlineIcon color="error" />Error</span>}
        maxWidth="sm"
        fullWidth
        actions={<Button variant="contained" onClick={handleCloseSnackbar}>OK</Button>}
      >
        <Alert severity="error" variant="outlined" sx={{ alignItems: 'flex-start', '& .MuiAlert-message': { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } }}>
          {snackbar.message}
        </Alert>
      </DraggableDialog>

      <CompanySelectionDialog
        open={showCompanyDialog}
        onClose={() => setShowCompanyDialog(false)}
        onCompanySelected={handleCompanySelected}
      />
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
